// Correspondances entre les identifiants du jeu et leurs textes.
import type { EventKind } from '../content/events';
import { SUBSCRIPTIONS } from '../content/subscriptions';
import type { TextKey } from '../content/fr';
import type { UpgradeCategory } from '../content/upgrades';
import { fmtEur, fmtPct } from '../engine/format';
import { t } from '../engine/i18n';
import type { MutuelleLevel, StageId, Subscription } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';

export const STAGE_LABEL: Record<StageId, TextKey> = {
  prologue: 'stage.prologue',
  stage1: 'stage.stage1',
  stage2: 'stage.stage2',
};

export const STAGE_INTRO: Record<StageId, { title: TextKey; text: TextKey }> = {
  prologue: { title: 'stageIntro.prologue.title', text: 'stageIntro.prologue.text' },
  stage1: { title: 'stageIntro.stage1.title', text: 'stageIntro.stage1.text' },
  stage2: { title: 'stageIntro.stage2.title', text: 'stageIntro.stage2.text' },
};

/** Le nom d'un abonnement en cours, avec son offre à publicités s'il y est passé. */
export function subscriptionLabel(sub: Subscription): string {
  const label = t(SUBSCRIPTIONS[sub.id].label);
  if (sub.shared) return t('sub.shared', { label });
  return sub.withAds ? t('sub.withAds', { label }) : label;
}

/** Ce qu'on explique, sous les envies, pour chaque abonnement qui en fait partie. */
export const PERK_HINT: Record<string, TextKey> = {
  cinema: 'budget.perk.cinema',
};

export const MUTUELLE_LABEL: Record<MutuelleLevel, TextKey> = {
  aucune: 'mutuelle.aucune',
  base: 'mutuelle.base',
  renforcee: 'mutuelle.renforcee',
};

export const KIND_LABEL: Record<EventKind, TextKey> = {
  imprevu: 'kind.imprevu',
  chance: 'kind.chance',
  etape: 'kind.etape',
  tentation: 'kind.tentation',
  marche: 'kind.marche',
  quotidien: 'kind.quotidien',
  travail: 'kind.travail',
  proches: 'kind.proches',
};

export const CATEGORY_LABEL: Record<UpgradeCategory, TextKey> = {
  outil: 'shop.category.outil',
  sante: 'shop.category.sante',
  formation: 'shop.category.formation',
};

const oneDecimal = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** Une durée de jeu en années, en mois ou en jours. */
export function formatDuration(days: number): string {
  if (days >= DAYS_PER_YEAR) {
    const years = oneDecimal.format(days / DAYS_PER_YEAR);
    return years === '1' ? t('common.oneYear') : t('common.years', { count: years });
  }
  if (days >= 30) return t('common.months', { count: Math.floor(days / 30.4) });
  const count = Math.max(0, Math.ceil(days));
  return count === 1 ? t('common.oneDay') : t('common.days', { count });
}

/** Un montant en euros précédé de son signe, pour les gains et les pertes. */
export function signedEur(value: number): string {
  return `${value > 0 ? '+' : ''}${fmtEur(value)}`;
}

/** Un pourcentage précédé de son signe. */
export function signedPct(value: number): string {
  return `${value > 0 ? '+' : ''}${fmtPct(value)}`;
}

/** Un nombre à une décimale au plus, à la française (« 17,2 », « 1 500 »). */
export function fmtNumber(value: number): string {
  return oneDecimal.format(value);
}
